import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useGetTopic } from "../../../api/queries";
import { useUpdateTopic } from "../../../api/mutations";
import { useNotification } from "../../../hooks/useNotification";
import Button from "../../../componentLibrary/Button";
import ConfirmationBox from "../../../componentLibrary/ConfirmationBox";
import Spinner from "../../../componentLibrary/Spinner";
import { ErrorDisplay } from "../../../utils/ErrorHandling";
import {
  topicAccentHex,
  topicCtaGradientStyle,
} from "../../../utils/topicBranding";
import Container from "../../../componentLibrary/Container";
import ProfileDefinitionSection from "./components/ProfileDefinitionSection";
import CheckinDefinitionSection from "./components/CheckinDefinitionSection";
import ToolsSection from "./components/ToolsSection";
import PlansSection from "./components/PlansSection";
import TopicPicker from "./components/TopicPicker";

const DEFAULT_BANNER_COLOR = "#7c5d9e";

export default function ToolboxManager() {
  const { topicId = "" } = useParams<{ topicId: string }>();
  const {
    data: selectedTopic,
    isError,
    isLoading,
    error,
  } = useGetTopic(topicId);
  const navigate = useNavigate();

  const [bannerColor, setBannerColor] = useState<string>(DEFAULT_BANNER_COLOR);
  const [isDirty, setIsDirty] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const updateMutation = useUpdateTopic({
    onSuccess: async () => {
      notify("success", "Toolbox saved successfully");
      setIsDirty(false);
      await queryClient.invalidateQueries({ queryKey: ["modules"] });
    },
    onError: () => {
      notify("error", "Failed to save toolbox");
    },
  });

  useEffect(() => {
    if (!selectedTopic) return;
    setBannerColor(
      selectedTopic.color
        ? topicAccentHex(selectedTopic.color)
        : DEFAULT_BANNER_COLOR,
    );
    setIsDirty(false);
  }, [topicId, selectedTopic?._id]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  if (isError) {
    return <ErrorDisplay error={error} messageSource="api" />;
  }

  const handleTopicSelect = (nextTopicSlug: string) => {
    navigate(`/admin/toolboxes/${nextTopicSlug}`);
  };

  const handleConfirmSave = () => {
    setSaveConfirmOpen(false);
    if (!selectedTopic) return;
    updateMutation.mutate({ ...selectedTopic, color: bannerColor });
  };

  if (!selectedTopic) {
    return (
      <div className="p-2 sm:p-4">
        <Container>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center">
            <p className="text-gray-700 font-medium">Topic not found</p>
            <p className="text-sm text-gray-500 mt-2">
              No toolbox matches this link. It may have been removed or the URL
              may be incorrect.
            </p>
            <Link
              to="/admin/toolboxes"
              className="inline-block mt-4 text-sm font-semibold text-electrum-300 hover:text-electrum-400"
            >
              Back to topic list
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  const topicTitle = selectedTopic.title;

  return (
    <>
      <div className="pt-4">
        <div className="mb-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
              Toolbox Manager
            </p>
            <Button
              type="button"
              size="sm"
              disabled={!isDirty}
              loading={updateMutation.isPending}
              onClick={() => setSaveConfirmOpen(true)}
            >
              {updateMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </div>

          <TopicPicker
            currentTopicId={topicId}
            selectedTopic={selectedTopic}
            onSelect={handleTopicSelect}
          />

          {selectedTopic.subtitle && (
            <p className="text-sm text-gray-600 mt-1 ml-11">
              {selectedTopic.subtitle}
            </p>
          )}
        </div>

        <div className="space-y-4 mt-4">
          {/* Banner with inline color edit trigger */}
          <div className="relative">
            <div
              className="rounded-xl p-5 text-white"
              style={topicCtaGradientStyle(bannerColor)}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold">How are you feeling?</h2>
                  <p className="text-sm text-white/80 mt-1">
                    Start tracking to see patterns in your {topicTitle} toolbox
                    over time.
                  </p>
                </div>
                <span className="inline-flex items-center justify-center font-bold h-10 text-base rounded-md whitespace-nowrap shadow bg-white border-2 !border-white py-2 px-4 opacity-60 cursor-default select-none">
                  <PlusIcon className="w-4 h-4 mr-1.5" />
                  Check In
                </span>
              </div>

              {/* Edit color button inside the banner */}
              <div className="mt-3 flex items-center gap-2">
                <label className="inline-flex items-center gap-1 text-xs font-semibold text-white/80 hover:text-white transition-colors cursor-pointer">
                  <PencilSquareIcon className="w-3.5 h-3.5" />
                  Edit color
                  <input
                    ref={colorInputRef}
                    type="color"
                    value={bannerColor}
                    onChange={(e) => {
                      setBannerColor(e.target.value);
                      setIsDirty(true);
                    }}
                    className="sr-only"
                  />
                </label>
                <span className="text-xs font-mono text-white/60">
                  {bannerColor}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-4">
            <div className="flex flex-col gap-4">
              <ProfileDefinitionSection
                topic={selectedTopic}
                topicId={topicId}
              />
              <ToolsSection topic={selectedTopic} />
            </div>

            <div className="flex flex-col gap-4">
              <PlansSection topicTitle={topicTitle} />
              <CheckinDefinitionSection
                topic={selectedTopic}
                topicId={topicId}
              />
            </div>
          </div>
        </div>
      </div>

      <ConfirmationBox
        isOpen={saveConfirmOpen}
        title="Save toolbox changes?"
        message="This will update the toolbox for all users."
        confirmText="Save"
        cancelText="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
        onCloseBox={() => setSaveConfirmOpen(false)}
      />
    </>
  );
}
